import React from 'react';
import { Landlord, Renter, PropertyAgent, Fsbo } from '../../../types/userTypes';
import ProfilePhoto from './ProfilePhoto';
import CommunicationPreferences from './CommunicationPreferences';
import ListingPreferences from './ListingPreferences';

interface ProfileFormProps {
  profile: Partial<Landlord | Renter | PropertyAgent | Fsbo>;
  isCreatingProfile: boolean;
  isUploading: boolean;
  onProfileChange: (field: string, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  profilePhoto: {
    photoUrl?: string;
    previewPhotoUrl?: string;
    onPhotoChange: (file: File) => void;
  };
  communicationPreferences: {
    preferences: {
      emailOffers: boolean;
      pushNotifications: boolean;
    };
    onPreferenceChange: (
      preference: 'emailOffers' | 'pushNotifications'
    ) => void;
  };
  listingPreferences: {
    preferences: {
      showEmail: boolean;
      showMobile: boolean;
    };
    onPreferenceChange: (preference: 'showEmail' | 'showMobile') => void;
  };
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  profile,
  isCreatingProfile,
  isUploading,
  onProfileChange,
  onSave,
  onCancel,
  profilePhoto,
  communicationPreferences,
  listingPreferences
}) => {
  return (
    <div className="bg-white shadow-lg rounded-xl overflow-hidden p-4 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center md:text-left">
        {isCreatingProfile ? 'Create Your Profile' : 'Edit Your Profile'}
      </h2>

      {/* Profile Photo Section */}
      <ProfilePhoto
        photoUrl={profilePhoto.photoUrl}
        previewPhotoUrl={profilePhoto.previewPhotoUrl}
        onPhotoChange={profilePhoto.onPhotoChange}
        isEditing={true}
        userName={profile.name || ''}
      />

      <div className="space-y-4 md:space-y-6">
        {/* Name Field */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
            Name
          </label>
          <input
            type="text"
            value={profile.name || ''}
            onChange={(e) => onProfileChange('name', e.target.value)}
            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            required
          />
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
            Email
          </label>
          <input
            type="email"
            value={profile.email || ''}
            onChange={(e) => onProfileChange('email', e.target.value)}
            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            required
            disabled={!isCreatingProfile}
          />
        </div>

        {/* Phone Field */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
            Phone
          </label>
          <input
            type="tel"
            value={profile.phone || ''}
            onChange={(e) => onProfileChange('phone', e.target.value)}
            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            required
          />
        </div>

        {/* Role Selection */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
            Your account type
          </label>
          <select
            value={profile.role || 'renter'}
            onChange={(e) =>
              onProfileChange(
                'role',
                e.target.value as 'landlord' | 'renter' | 'agent'
              )
            }
            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            required
          >
            <option value="renter">Renter</option>
            <option value="landlord">Landlord</option>
            <option value="agent">Property Agent</option>
          </select>
        </div>

        {/* Conditional Field: Company (Landlord) */}
        {(profile.role === 'agent' || profile.role === 'landlord') && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
              Company
            </label>
            <input
              type="text"
              value={(profile as Landlord).company || ''}
              onChange={(e) => onProfileChange('company', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              required
            />
          </div>
        )}

        {/* PSRA Licence for agents */}
        {(profile.role === 'agent' || profile.role === 'landlord') && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
              PSRA Licence
            </label>
            <input
              type="text"
              value={(profile as PropertyAgent).psraLicence || ''}
              onChange={(e) => onProfileChange('psraLicence', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              required
            />
          </div>
        )}

        {/* Conditional Field: Preferred Location (Renter) */}
        {profile.role === 'renter' && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
              Preferred Location
            </label>
            <input
              type="text"
              value={(profile as Renter).preferredLocation || ''}
              onChange={(e) =>
                onProfileChange('preferredLocation', e.target.value)
              }
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              required
            />
          </div>
        )}

        {/* Bio Field */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 text-sm md:text-base">
            Bio
          </label>
          <textarea
            value={profile.bio || ''}
            onChange={(e) => onProfileChange('bio', e.target.value)}
            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            rows={4}
            required
          ></textarea>
        </div>

        {/* Communication Preferences */}
        <CommunicationPreferences
          preferences={communicationPreferences.preferences}
          onPreferenceChange={communicationPreferences.onPreferenceChange}
          isEditing={true}
        />

        {/* Listing Preferences */}
        <ListingPreferences
          preferences={listingPreferences.preferences}
          onPreferenceChange={listingPreferences.onPreferenceChange}
          isEditing={true}
        />
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4">
        <button
          onClick={onSave}
          disabled={isUploading}
          className={`bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-all duration-300 w-full md:w-auto text-sm md:text-base ${
            isUploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isUploading
            ? 'Saving...'
            : isCreatingProfile
              ? 'Create Profile'
              : 'Save Changes'}
        </button>
        <button
          onClick={onCancel}
          disabled={isUploading}
          className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400 transition-all duration-300 w-full md:w-auto text-sm md:text-base"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ProfileForm;
